import {
  ActivityKindEnum,
  PropertyKindEnum,
  TransactionTypeEnum,
  CompletionStatusEnum,
  AdSummary,
  UserModel,
} from '@/types';
import ReactPDF, { Page, StyleSheet, Text, View, Image } from '@react-pdf/renderer';
import { Section } from './Section';
import { Badge, IconText, InfoText } from './Badge';
import { FeaturesSection } from './FeaturesSection';
import { GallerySection } from './GallerySection';
import { AddressIconText } from './AddressIconText';
import { Price } from './Price';

import {
  BuildingsIcon,
  CurrencyCircleDollarWhiteIcon,
  HouseLineIcon,
  LocationWhiteIcon,
  TitlePDF,
  GradientOverlay,
  DeveloperIcon,
  SellerIcon,
  PostHandoverIcon,
  HandoverIcon,
} from '@/assets/pdf';

ReactPDF.Font.register({
  family: 'Poppins',
  src: '/assets/fonts/Poppins-Regular.ttf',
});

ReactPDF.Font.register({
  family: 'Poppins',
  fontWeight: 'bold',
  src: '/assets/fonts/Poppins-Bold.ttf',
});

const baseStyles = StyleSheet.create({
  poppinsBold: {
    fontFamily: 'Poppins',
    fontWeight: 'bold',
  },
  informationInsideImage: {
    display: 'flex',
    borderRadius: 30,
    position: 'absolute',
    padding: 23,
  },
  blockTitleImageText: {
    color: '#FFF',
    display: 'flex',
    flexDirection: 'row',
  },
  featureBlock: {
    width: '48vw',
    color: '#1650FF',
  },
});

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Poppins',
    fontSize: 40,

    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
  },
  bold: {
    fontSize: 80,
    ...baseStyles.poppinsBold,
  },
  price: {
    fontSize: 96,
    lineHeight: 1.4,
    color: '#1650FF',
    ...baseStyles.poppinsBold,
  },
  inline: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 32,
    color: 'FFF',
  },
  blockTitleImageText: {
    fontSize: 75,
    maxWidth: '1800px',
    ...baseStyles.poppinsBold,
    ...baseStyles.blockTitleImageText,
  },
  blocksInsideImageWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  priceWrapper: {
    ...baseStyles.poppinsBold,
    ...baseStyles.blockTitleImageText,
  },
  image: {
    height: 2600,
    objectFit: 'cover',
    position: 'relative',
  },
  mainImageWrapper: {
    flexDirection: 'column',
    gap: '8px',
    bottom: '50px',
    margin: 'auto',
    left: 0,
    textAlign: 'center',
    right: 0,
    ...baseStyles.informationInsideImage,
  },
  locationBlock: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'center',
    backgroundColor: '#1650FF',
    padding: '8px 19px 9px 20px',
    gap: '8px',
  },
  secondFeatureBlock: {
    display: 'flex',
    paddingLeft: '200px',
    ...baseStyles.featureBlock,
  },
  firstFeatureBlock: {
    ...baseStyles.featureBlock,
  },
  informationWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#E9EEF1',
    padding: '30px 40px',
    margin: '0 -40px',
  },
  sellingBlockWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#E9EEF1',
    padding: '50px 40px',
    margin: '0 -40px',
    gap: 32,
  },
});

interface IPdfDocument {
  showContent: SelectedDataType;
  adsData: AdSummary;
  personalInfo: UserModel;
}

type SelectedDataType = {
  isAgencyInformationVisible?: boolean;
  isBrokersInformationVisible?: boolean;
  showPrice?: boolean;
};

export function PortableView({ showContent, adsData, personalInfo }: IPdfDocument) {
  const {
    addressEn,
    defaultRentFrequency,
    activityKind,
    convertedPrice,
    convertedPriceYearly,
    convertedPriceMonthly,
    convertedPriceWeekly,
    convertedPriceDaily,
    convertedCurrency,
    floor,
    propertyKind,
    completionStatus,
    convertedArea,
    plotArea,
    convertedAreaUnits,
    baths,
    beds,
    description,
    toSeller,
    toDeveloper,
    handover,
    postHandover,
    transactionType,
    view,
    convertedSellingPrice,
    coordinatesLat,
    coordinatesLong,
    features,
    gallery,
  } = adsData;

  const A4_WIDTH = 300;
  const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN as string;

  const bedsAndBathsIsVisible = [
    PropertyKindEnum.Apartment,
    PropertyKindEnum.Villa,
    PropertyKindEnum.CommercialVilla,
    PropertyKindEnum.Townhouse,
    PropertyKindEnum.Penthouse,
    PropertyKindEnum.HotelApartment,
  ].includes(propertyKind);

  const propertyKindLabels = {
    [PropertyKindEnum.Apartment]: 'Apartment',
    [PropertyKindEnum.Townhouse]: 'Townhouse',
    [PropertyKindEnum.Villa]: 'Villa',
    [PropertyKindEnum.Floor]: 'Floor',
    [PropertyKindEnum.Penthouse]: 'Penthouse',
    [PropertyKindEnum.ResidentialLand]: 'Residential Land',
    [PropertyKindEnum.VillaCompound]: 'Villa Compound',
    [PropertyKindEnum.HotelApartment]: 'Hotel Apartment',
    [PropertyKindEnum.CommercialVilla]: 'Commercial Villa',
    [PropertyKindEnum.Warehouse]: 'Warehouse',
    [PropertyKindEnum.IndustrialLand]: 'Industrial Land',
    [PropertyKindEnum.CommercialLand]: 'Commercial Land',
    [PropertyKindEnum.Office]: 'Office',
    [PropertyKindEnum.LabourCamp]: 'Labour Camp',
    [PropertyKindEnum.CommercialBuilding]: 'Commercial Building',
    [PropertyKindEnum.Shop]: 'Shop',
  };

  const isSale = activityKind !== ActivityKindEnum.Rent;
  const textBeds = beds === 'Studio' ? '' : `${beds}-bedroom`;

  const textArea = `${plotArea ? `${plotArea} / ` : ''}${convertedArea ?? ''} ${convertedAreaUnits?.toLowerCase() ?? ''}`;
  const presentationText = `${bedsAndBathsIsVisible ? textBeds : ''} ${propertyKindLabels[propertyKind]}, `;

  const isSellingBlockVisible =
    transactionType === TransactionTypeEnum.Resale ||
    (transactionType === TransactionTypeEnum.Distress &&
      completionStatus === CompletionStatusEnum.OffPlan);

  const getFloorText = () => {
    if (floor) {
      return `${capitalizeFirstLetter(floor)}`;
    }
    return null;
  };

  const floorText = getFloorText();

  return (
    <Page size={[A4_WIDTH]} dpi={300} style={styles.page}>
      <View style={{ display: 'flex', flexDirection: 'column', gap: '50px' }}>
        <View style={{ display: 'flex', flexDirection: 'column', gap: '50px' }}>
          <View>
            <View>
              <Image src={TitlePDF} style={styles.image} />
              <Image
                src={GradientOverlay}
                style={{
                  marginTop: -2600,
                  height: 2600,
                }}
              />
              <View
                style={{
                  flexDirection: 'row',
                  gap: 16,
                  position: 'absolute',
                  right: 40,
                  top: 40,
                }}
              >
                {!isSale && (
                  <Badge iconSrc={CurrencyCircleDollarWhiteIcon}>
                    {defaultRentFrequency
                      ? `${capitalizeFirstLetter(activityKind)}/${defaultRentFrequency}`
                      : capitalizeFirstLetter(activityKind)}
                  </Badge>
                )}
                {transactionType && (
                  <Badge iconSrc={HouseLineIcon}>{capitalizeFirstLetter(transactionType)}</Badge>
                )}
                {propertyKind && (
                  <Badge iconSrc={BuildingsIcon}>
                    {capitalizeFirstLetter(propertyKindLabels[propertyKind])}
                  </Badge>
                )}
              </View>
              {view && view?.length > 0 && (
                <View
                  style={{
                    flexDirection: 'column',
                    color: '#fff',
                    fontSize: '40px',
                    alignItems: 'flex-end',
                    gap: 10,
                    position: 'absolute',
                    right: 40,
                    top: 150,
                  }}
                >
                  <Text style={{ fontWeight: 600 }}>View:</Text>
                  {view.map((elementView) => {
                    return <Text key={elementView}>{`${elementView}`}</Text>;
                  })}
                </View>
              )}
            </View>
            <View style={styles.mainImageWrapper}>
              <View style={styles.blocksInsideImageWrapper}>
                <Text style={styles.blockTitleImageText}>Presentation of</Text>
                <Text style={styles.blockTitleImageText}>{presentationText}</Text>
                <Text style={styles.blockTitleImageText}>{textArea}</Text>
              </View>
            </View>
          </View>

          <View style={{ gap: 40, padding: '0 40px' }}>
            <AddressIconText iconSrc={LocationWhiteIcon}>{addressEn}</AddressIconText>
            <View style={styles.informationWrapper}>
              <InfoText title='area'>{textArea}</InfoText>
              {beds && <InfoText title='bedrooms'>{beds}</InfoText>}
              {baths && <InfoText title='baths'>{baths}</InfoText>}
              {floorText && <InfoText title='floor'>{floorText}</InfoText>}
            </View>
            {isSellingBlockVisible && (
              <View style={styles.sellingBlockWrapper}>
                <View style={{ flexDirection: 'column', gap: 20 }}>
                  {toSeller && (
                    <IconText iconSrc={SellerIcon}>
                      To seller:{' '}
                      <Text style={{ color: '#1650FF', fontWeight: 500 }}>
                        {numberFormat.format(toSeller)} {convertedCurrency}
                      </Text>
                    </IconText>
                  )}
                  {toDeveloper && (
                    <IconText iconSrc={DeveloperIcon}>
                      To developer:{' '}
                      <Text style={{ color: '#1650FF', fontWeight: 500 }}>
                        {numberFormat.format(toDeveloper)} {convertedCurrency}
                      </Text>
                    </IconText>
                  )}
                </View>
                <View style={{ flexDirection: 'column', gap: 20 }}>
                  {postHandover && (
                    <IconText iconSrc={PostHandoverIcon}>
                      Post handover: <Text style={{ color: '#1650FF', fontWeight: 500 }}>Yes</Text>
                    </IconText>
                  )}
                  {handover && (
                    <IconText iconSrc={HandoverIcon}>
                      Handover:{' '}
                      <Text style={{ color: '#1650FF', fontWeight: 500 }}>
                        {handover.quarter} / {handover.year}
                      </Text>
                    </IconText>
                  )}
                </View>
              </View>
            )}
            {showContent.showPrice ? (
              <Price
                isSale={isSale}
                convertedPrice={convertedPrice}
                convertedPriceYearly={convertedPriceYearly}
                convertedPriceMonthly={convertedPriceMonthly}
                convertedPriceWeekly={convertedPriceWeekly}
                convertedPriceDaily={convertedPriceDaily}
                convertedCurrency={convertedCurrency}
                convertedSellingPrice={convertedSellingPrice}
                transactionType={transactionType}
              />
            ) : null}
            {gallery && <GallerySection wrap={true} title='Gallery' images={gallery} />}
          </View>
        </View>
        <View style={{ padding: 40, gap: 40 }}>
          <FeaturesSection data={features} />
          <Section title={'Map'} wrap={true}>
            <Image
              src={`https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/pin-s+ff0000(${coordinatesLong},${coordinatesLat})/${coordinatesLong},${coordinatesLat},16/800x800?access_token=${mapboxToken}`}
              style={{
                width: '100%',
                height: 800,
                borderRadius: 20,
                objectFit: 'cover',
              }}
            />
          </Section>
          {description && (
            <Section title={'Description'} wrap={true}>
              <Text style={{ fontSize: '50', width: '98%', textAlign: 'justify' }}>
                {description}
              </Text>
            </Section>
          )}
        </View>
      </View>
    </Page>
  );
}

const Separator = () => (
  <View
    style={{
      height: '150px',
      width: 4,
      backgroundColor: '#1650FF',
      marginHorizontal: 0,
    }}
  />
);

const numberFormat = new Intl.NumberFormat('en', {
  maximumSignificantDigits: 3,
});

const capitalizeFirstLetter = (string?: string) => {
  if (!string) return string;
  return string.charAt(0).toUpperCase() + string.slice(1);
};
